import { useReducer, useEffect, useMemo, useCallback } from 'react';
import { graphQL } from 'javascripts/shared/graphql';
import { graphQLUtils } from 'javascripts/shared/utils/graphql_utils';

const SUCCEEDED = 'succeeded';
const FAILED = 'failed';
const LOADING = 'loading';

const INITIAL_REQUEST_STATE = {
  loading: true,
  data: null,
  errors: null,
};

export function useGraphQLClientData(query, variables = {}, { preserveDataWhenReloading = false } = {}) {
  // stringifying here so that react can do a deep equality check (aka also check nested objects, like arrays).
  // This is fine for relatively small objects (like query params), but be wary of doing it with large objects
  // https://twitter.com/dan_abramov/status/1104414272753487872
  const memoizedVariables = useMemo(
    () => variables,
    Object.values(variables).map(variable => JSON.stringify(variable)),
  );

  // Why use useReducer instead of useState here? useReducer lets you set related state at the same time.
  // With useState, each call to setState can cause a rerender if they happen in an async call, like in a promise.
  // (react can batch those calls when they happen outside of an async call, like directly in the callback given to a component)
  const [{ data, errors, loading }, setState] = useReducer((currentState, action) => {
    switch (action.state) {
      case LOADING:
        return { loading: true, data: preserveDataWhenReloading ? currentState.data : null, errors: null };
      case SUCCEEDED:
        return { loading: false, data: action.data, errors: null };
      case FAILED:
        return { loading: false, data: null, errors: action.errors };
      default:
        throw new Error(
          'Unknown Graphql client state. If this happens useGraphQLClientData#setState is getting called with a state value not in the switch.',
        );
    }
  }, INITIAL_REQUEST_STATE);

  const fetchDataWithGraphQLClient = useCallback(() => {
    graphQL
      .get(query, memoizedVariables)
      .then(response => {
        setState({ state: SUCCEEDED, data: response });
      })
      .catch(error => {
        const graphQLErrors = graphQLUtils.errorsFromGraphQLRequestError(error);

        if (graphQLErrors.length > 0) {
          // We've received a GraphQL layer error, propogate error state through stack
          setState({ state: FAILED, errors: graphQLErrors });
        } else {
          // We've received another, unrelated error - raise this up for reporting/handling above
          throw error;
        }
      });
  }, [query, memoizedVariables]);

  useEffect(() => {
    setState({ state: LOADING });
    fetchDataWithGraphQLClient();
  }, [fetchDataWithGraphQLClient]);

  return {
    loading,
    forceRefreshData: fetchDataWithGraphQLClient,
    errors,
    ...data,
  };
}
